import React, { useEffect, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalSection = {
  heading?: string;
  paragraphs?: string[];
  bullets?: string[];
};
type LegalDoc = {
  title: string;
  lastUpdated: string;
  sections: LegalSection[];
};
export type LegalDocType = 'terms' | 'privacy';
const TERMS: LegalDoc = {
  title: 'Terms of Service',
  lastUpdated: '6/10/2026',
  sections: [{
    paragraphs: ['Welcome to Mike\u2019s Pro Web Design. By purchasing or using our website design, hosting, maintenance, or related services, you agree to the following Terms of Service.']
  }, {
    heading: '1. Services',
    paragraphs: ['Mike\u2019s Pro Web Design provides website design, website development, website maintenance, hosting management, and related digital services. All services are subject to availability and project requirements.']
  }, {
    heading: '2. Client Responsibilities',
    paragraphs: ['Clients agree to:'],
    bullets: ['Provide accurate information required for project completion.', 'Supply text, images, logos, and other content in a timely manner.', 'Ensure they have legal rights to any content submitted for use on their website.', 'Review and approve project milestones promptly.']
  }, {
    paragraphs: ['Delays in providing required materials may result in project delays.']
  }, {
    heading: '3. Project Timeline',
    paragraphs: ['Estimated completion dates are provided as guidelines and may change due to:'],
    bullets: ['Delayed client feedback', 'Content submission delays', 'Scope changes', 'Technical issues beyond our control']
  }, {
    paragraphs: ['Mike\u2019s Pro Web Design will make reasonable efforts to meet agreed timelines.']
  }, {
    heading: '4. Revisions',
    paragraphs: ['Each project includes the number of revisions specified in the selected service package.', 'Additional revisions, redesign requests, or scope changes beyond the included revisions may incur additional fees.']
  }, {
    heading: '5. Payments',
    bullets: ['Payment terms are outlined in each project proposal or invoice.', 'Deposits are non-refundable once project work has begun.', 'Final files and website launch may be withheld until all outstanding balances are paid in full.', 'Failure to pay invoices may result in suspension of services.']
  }, {
    heading: '6. Hosting Services',
    paragraphs: ['If hosting services are provided through Mike\u2019s Pro Web Design:'],
    bullets: ['Hosting fees are billed monthly or annually.', 'Hosting services may be suspended for unpaid balances.', 'Clients are responsible for maintaining current payment information.', 'Mike\u2019s Pro Web Design is not responsible for interruptions caused by third-party hosting providers.']
  }, {
    heading: '7. Ownership and Intellectual Property',
    paragraphs: ['Upon full payment:'],
    bullets: ['Clients own the final website content they have provided.', 'Clients receive rights to the completed website design unless otherwise specified.', 'Mike\u2019s Pro Web Design retains the right to display completed projects in its portfolio and marketing materials.']
  }, {
    paragraphs: ['Third-party software, plugins, themes, and licenses remain subject to their respective licensing agreements.']
  }, {
    heading: '8. Website Maintenance',
    paragraphs: ['Maintenance plans cover only services specifically listed in the maintenance package.', 'Additional work requested outside the maintenance agreement may require separate billing.']
  }, {
    heading: '9. Limitation of Liability',
    paragraphs: ['Mike\u2019s Pro Web Design shall not be liable for:'],
    bullets: ['Lost profits', 'Business interruption', 'Data loss', 'Third-party service outages', 'Indirect or consequential damages']
  }, {
    paragraphs: ['Our total liability shall not exceed the amount paid by the client for the services directly related to the claim.']
  }, {
    heading: '10. Third-Party Services',
    paragraphs: ['Websites may utilize third-party services, including payment processors, hosting providers, plugins, analytics tools, and other software.', 'Mike\u2019s Pro Web Design is not responsible for outages, policy changes, pricing changes, or functionality issues caused by third-party providers.']
  }, {
    heading: '11. Termination',
    paragraphs: ['Either party may terminate services with written notice.', 'Clients remain responsible for payment of all completed work and services rendered prior to termination.']
  }, {
    heading: '12. Refund Policy',
    paragraphs: ['Due to the custom nature of website design services:'],
    bullets: ['Deposits are non-refundable.', 'Completed work is generally non-refundable.', 'Refund requests will be reviewed on a case-by-case basis.']
  }, {
    heading: '13. Changes to These Terms',
    paragraphs: ['Mike\u2019s Pro Web Design reserves the right to modify these Terms of Service at any time. Updated versions will be posted on this page with a revised effective date.']
  }, {
    heading: '14. Contact Information',
    paragraphs: ['For questions regarding these Terms of Service, please contact:', 'Mike\u2019s Pro Web Design', 'Email: mikesproweb@gmail.com', 'Phone: (818) 381-9905', 'Website: www.mikesproweb.com']
  }]
};
const PRIVACY: LegalDoc = {
  title: 'Privacy Policy',
  lastUpdated: '6/10/2026',
  sections: [{
    paragraphs: ['Mike\u2019s Pro Web Design respects your privacy and is committed to protecting the information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.']
  }, {
    heading: '1. Information We Collect',
    paragraphs: ['We may collect the following information:', 'Personal Information'],
    bullets: ['Name', 'Email address', 'Phone number', 'Business name', 'Billing information', 'Any information submitted through contact forms or service requests']
  }, {
    paragraphs: ['Technical Information'],
    bullets: ['IP address', 'Browser type', 'Device information', 'Operating system', 'Pages visited', 'Referring websites', 'Usage data and analytics information']
  }, {
    heading: '2. How We Use Your Information',
    paragraphs: ['We may use collected information to:'],
    bullets: ['Provide website design and related services', 'Respond to inquiries and support requests', 'Process payments and invoices', 'Improve our website and services', 'Send project updates and communications', 'Comply with legal obligations', 'Prevent fraud and unauthorized activity']
  }, {
    heading: '3. Cookies and Tracking Technologies',
    paragraphs: ['Our website may use cookies and similar technologies to:'],
    bullets: ['Improve website functionality', 'Analyze visitor behavior', 'Remember user preferences', 'Measure website performance']
  }, {
    paragraphs: ['Visitors can manage cookie preferences through their browser settings.']
  }, {
    heading: '4. Sharing of Information',
    paragraphs: ['Mike\u2019s Pro Web Design does not sell personal information.', 'We may share information with trusted third-party service providers when necessary to operate our business, including:'],
    bullets: ['Payment processors', 'Website hosting providers', 'Email service providers', 'Analytics providers', 'Security and maintenance providers']
  }, {
    paragraphs: ['These providers are only given access to information necessary to perform their services.']
  }, {
    heading: '5. Data Security',
    paragraphs: ['We implement reasonable technical and organizational measures to protect personal information from unauthorized access, disclosure, alteration, or destruction.', 'However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.']
  }, {
    heading: '6. Third-Party Services',
    paragraphs: ['Our website may contain links to third-party websites or services.', 'We are not responsible for the privacy practices, policies, or content of third-party websites. Users should review the privacy policies of any external sites they visit.']
  }, {
    heading: '7. Data Retention',
    paragraphs: ['We retain personal information only as long as necessary to:'],
    bullets: ['Provide services', 'Fulfill contractual obligations', 'Resolve disputes', 'Comply with legal requirements', 'Enforce our agreements']
  }, {
    paragraphs: ['When information is no longer needed, it will be securely deleted or anonymized where appropriate.']
  }, {
    heading: '8. Your Privacy Rights',
    paragraphs: ['Depending on your location, you may have rights regarding your personal information, including:'],
    bullets: ['Access to your personal data', 'Correction of inaccurate information', 'Deletion of personal information', 'Restriction of processing', 'Objection to certain data uses', 'Data portability where applicable']
  }, {
    paragraphs: ['Requests regarding personal data may be submitted using the contact information below.']
  }, {
    heading: '9. Children\u2019s Privacy',
    paragraphs: ['Our services are not directed toward children under the age of 13.', 'We do not knowingly collect personal information from children. If we become aware that information has been collected from a child, we will take appropriate steps to remove it.']
  }, {
    heading: '10. California Privacy Rights',
    paragraphs: ['If you are a California resident, you may have additional privacy rights under applicable California privacy laws.', 'California residents may request information regarding the categories of personal information collected, used, or disclosed, subject to applicable legal requirements.']
  }, {
    heading: '11. Changes to This Privacy Policy',
    paragraphs: ['We reserve the right to update this Privacy Policy at any time.', 'Changes become effective immediately upon posting. The updated version will include a revised "Last Updated" date.']
  }, {
    heading: '12. Contact Information',
    paragraphs: ['If you have any questions regarding this Privacy Policy or your personal information, please contact:', 'Mike\u2019s Pro Web Design', 'Email: mikesproweb@gmail.com', 'Phone: (818) 381-9905', 'Website: www.mikesproweb.com']
  }]
};
const DOCS: Record<LegalDocType, LegalDoc> = {
  terms: TERMS,
  privacy: PRIVACY
};
interface LegalModalProps {
  doc: LegalDocType | null;
  onClose: () => void;
}
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (!doc) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [doc, onClose]);
  const content = doc ? DOCS[doc] : null;
  return <AnimatePresence data-id="element-198">
      {content && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" aria-modal="true" role="dialog" aria-labelledby="legal-modal-title" data-id="element-199">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-200" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.97,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-[#111118] border border-white/10 rounded-3xl shadow-2xl" data-id="element-201">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 p-8 pb-4 border-b border-white/10" data-id="element-202">
              <div data-id="element-203">
                <h3 id="legal-modal-title" className="text-2xl font-heading font-bold text-white" data-id="element-204">
                  {content.title}
                </h3>
                <p className="text-sm text-slate-400 mt-1" data-id="element-205">
                  Last Updated: {content.lastUpdated}
                </p>
              </div>
              <button onClick={onClose} aria-label="Close" className="flex-shrink-0 text-slate-400 hover:text-white transition-colors mt-1" data-id="element-206">
                <X size={22} data-id="element-207" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto p-8 pt-6 space-y-6" data-id="element-208">
              {content.sections.map((section, i) => <div key={i} className="space-y-3" data-id="element-209">
                  {section.heading && <h4 className="text-lg font-bold text-white" data-id="element-210">
                      {section.heading}
                    </h4>}
                  {section.paragraphs?.map((p, pi) => <p key={pi} className="text-sm leading-relaxed text-white" data-id="element-211">
                      {p}
                    </p>)}
                  {section.bullets && <ul className="list-disc pl-5 space-y-2" data-id="element-212">
                      {section.bullets.map((b, bi) => <li key={bi} className="text-sm leading-relaxed text-white marker:text-blue-400" data-id="element-213">
                          {b}
                        </li>)}
                    </ul>}
                </div>)}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}